import React, { useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { ExternalLink } from 'lucide-react';
import { BDAY_EASE } from '../theme';
import type { BirthdayContent } from '../content';
import { useRsvp } from '@/src/modules/rsvp';
import type { RsvpStatus } from '@/src/modules/rsvp';

interface RsvpSectionProps {
  rsvp: BirthdayContent['rsvp'];
  invitationId?: string | null;
  isLive?: boolean;
}

const STATUS_OPTIONS: { value: RsvpStatus; label: string }[] = [
  { value: 'ACCEPTED', label: "I'll be there 🎉" },
  { value: 'MAYBE', label: 'Maybe 🤔' },
  { value: 'DECLINED', label: "Can't make it 😢" },
];

export function RsvpSection({ rsvp, invitationId, isLive }: RsvpSectionProps) {
  const [showForm, setShowForm] = useState(false);
  const [guestName, setGuestName] = useState('');
  const [guestEmail, setGuestEmail] = useState('');
  const [status, setStatus] = useState<RsvpStatus>('ACCEPTED');
  const [headcount, setHeadcount] = useState(1);

  const { state, errorMessage, submit, reset } = useRsvp(invitationId, isLive ?? false);
  const done = state === 'success';
  const sending = state === 'loading';

  const handleRsvp = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!guestName.trim() || sending) return;

    await submit({
      guestName: guestName.trim(),
      guestEmail: guestEmail.trim() || undefined,
      status,
      headcount: status === 'DECLINED' ? 1 : headcount,
    });
  };

  const fieldClass =
    'w-full rounded-xl border border-blue-400/20 bg-white/5 px-4 py-3 text-white placeholder:text-blue-400/40 outline-none focus:border-blue-400/60 focus:ring-2 focus:ring-blue-400/10 transition-all text-sm';

  return (
    <section
      className="relative py-24 px-6 overflow-hidden text-center"
      style={{ background: 'linear-gradient(160deg, #0f172a 0%, #1e3a5f 60%, #1d4ed8 100%)' }}
    >

      {/* glow blob */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 rounded-full bg-blue-600/20 blur-3xl pointer-events-none" />

      <div className="relative max-w-xl mx-auto flex flex-col items-center gap-8">
        {/* custom message */}
        {rsvp.message && (
          <motion.blockquote
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.15 }}
            className="text-blue-200 italic text-base leading-relaxed border-l-2 border-blue-400/40 pl-5 text-left"
          >
            "{rsvp.message}"
          </motion.blockquote>
        )}

        <AnimatePresence mode="wait">
          {done ? (
            <motion.div
              key="success"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, ease: BDAY_EASE }}
              className="bg-blue-500/15 border border-blue-400/30 rounded-3xl px-8 py-10 w-full"
            >
              <p className="text-5xl mb-4">🎉</p>
              <p className="text-white font-bold text-2xl">Thank you{guestName ? `, ${guestName}` : ''}!</p>
              <p className="text-blue-200 text-sm mt-2">
                {status === 'ACCEPTED'
                  ? "We can't wait to celebrate with you!"
                  : status === 'MAYBE'
                    ? "We hope to see you there!"
                    : "We'll miss you — thank you for letting us know."}
              </p>
              <button
                type="button"
                onClick={() => { reset(); setGuestName(''); setGuestEmail(''); setShowForm(false); }}
                className="mt-4 text-blue-400 text-xs underline hover:text-blue-200 transition-colors"
              >
                Change my response
              </button>
            </motion.div>
          ) : !showForm ? (
            /* ── Gate card ── */
            <motion.div
              key="gate"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95 }}
              transition={{ duration: 0.5, ease: BDAY_EASE }}
              className="bg-white/8 backdrop-blur-sm border border-blue-400/20 rounded-3xl p-10 w-full flex flex-col items-center gap-6"
            >
              <p className="text-5xl">🎂</p>
              <div>
                <h3 className="text-white font-bold text-2xl mb-1">Will You Join Us?</h3>
                <p className="text-blue-300 text-sm">Let the birthday star know you're coming!</p>
              </div>
              <motion.button
                whileHover={{ scale: 1.04 }}
                whileTap={{ scale: 0.97 }}
                type="button"
                onClick={() => setShowForm(true)}
                className="inline-flex items-center justify-center gap-3 px-10 py-3.5 bg-gradient-to-r from-blue-500 to-blue-700 text-white font-bold uppercase tracking-widest text-sm rounded-full shadow-xl shadow-blue-900/60 hover:from-blue-400 hover:to-blue-600 transition-all"
              >
                🎉 RSVP Now
              </motion.button>
            </motion.div>
          ) : (
            /* ── RSVP form ── */
            <motion.form
              key="form"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.5, ease: BDAY_EASE }}
              onSubmit={handleRsvp}
              className="bg-white/8 backdrop-blur-sm border border-blue-400/20 rounded-3xl p-6 w-full flex flex-col gap-4 text-left"
            >
              <p className="text-blue-300 text-xs font-bold uppercase tracking-widest text-center">Confirm your attendance</p>
              <input
                className={fieldClass}
                placeholder="Your name *"
                value={guestName}
                onChange={(e) => setGuestName(e.target.value)}
                maxLength={120}
                required
              />
              <input
                type="email"
                className={fieldClass}
                placeholder="Email (optional)"
                value={guestEmail}
                onChange={(e) => setGuestEmail(e.target.value)}
                maxLength={255}
              />
              <div className="flex flex-wrap gap-2">
                {STATUS_OPTIONS.map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setStatus(opt.value)}
                    className={`flex-1 min-w-[120px] rounded-xl border px-3 py-2.5 text-sm font-semibold transition-all ${
                      status === opt.value
                        ? 'border-blue-400 bg-blue-500/30 text-white'
                        : 'border-blue-400/20 bg-white/5 text-blue-200 hover:border-blue-400/50'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
              {status !== 'DECLINED' && (
                <label className="flex items-center justify-between gap-3 text-blue-200 text-sm">
                  <span>How many of you?</span>
                  <input
                    type="number"
                    min={1}
                    max={20}
                    value={headcount}
                    onChange={(e) => setHeadcount(Math.max(1, Number(e.target.value) || 1))}
                    className="w-20 rounded-xl border border-blue-400/20 bg-white/5 px-3 py-2 text-white text-center outline-none focus:border-blue-400/60"
                  />
                </label>
              )}
              {state === 'error' && errorMessage && (
                <p className="text-red-300 text-xs font-semibold">{errorMessage}</p>
              )}
              <button
                type="submit"
                disabled={sending}
                className="inline-flex items-center justify-center gap-3 px-10 py-3.5 bg-gradient-to-r from-blue-500 to-blue-700 text-white font-bold uppercase tracking-widest text-sm rounded-full shadow-xl shadow-blue-900/60 hover:from-blue-400 hover:to-blue-600 transition-all disabled:cursor-not-allowed disabled:opacity-60"
              >
                {sending ? 'Sending…' : '🎉 Send RSVP'}
              </button>
              {rsvp.link && (
                <a
                  href={rsvp.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-1 text-blue-400 text-xs hover:text-blue-200 transition-colors"
                >
                  <ExternalLink size={12} />
                  Use external RSVP form
                </a>
              )}
            </motion.form>
          )}
        </AnimatePresence>
      </div>
    </section>
  );
}
